import React from 'react';
import { Combobox as HUICombobox, Transition } from '@headlessui/react';
import Popover from 'lib/components/popover';
import { cn, mergeClasses } from 'lib/helpers/class';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { isFunction, utilizeAccessor } from 'lib/utils/function';
import { matchReferenceWidth } from 'lib/helpers/floating-ui';
import { ConditionalWrapper } from 'lib/components/conditional-render.jsx';
import { isString } from 'lib/utils/string';
import { BouncingDots } from 'lib/components/loading';
import { undefineIf } from 'lib/utils/undefined';
import { getPopInProps } from 'lib/helpers/transition';
import useDocumentEvent from 'lib/hooks/use-document-event';
import { Icon } from '@iconify-icon/react';

// TODO: Remove utilize accessor

const ComboboxContext = React.createContext();
ComboboxContext.displayName = 'ComboboxContext';

const useCombobox = () => React.useContext(ComboboxContext);
const PopoverWrapper = (props) => {
    const { isOpen, children, ...rest } = props;

    return (
        <Popover middlewares={[matchReferenceWidth()]} isOpen={isOpen}>
            <Popover.Reference {...rest}>{children}</Popover.Reference>
        </Popover>
    );
};

/**
 * @see https://headlessui.com/react/combobox#component-api
 */
function Elements(props) {
    const { className: providedClassName, noBorder = false, as = 'div', children, ...rest } = props;

    const { hasError, disabled } = useCombobox();
    const hasBorder = !noBorder;
    const className = mergeClasses(
        cn(
            'group relative flex justify-between w-full rounded-md focus-within:ring-1 focus-within:ring-primary-500',
            hasError &&
                hasBorder &&
                'border-red-400 text-red-800 placeholder-red-300 focus-within:border-transparent focus-within:ring-red-500',
            hasBorder && 'border border-neutral-300 focus-within:border-primary-500 shadow-sm',
            disabled ? 'bg-neutral-50 opacity-80' : 'bg-white'
        ),
        providedClassName
    );

    const { isOpen, inPortal } = useCombobox();

    return (
        <ConditionalWrapper
            element={[PopoverWrapper, as]}
            condition={inPortal}
            className={className}
            isOpen={undefineIf(isOpen, !inPortal)}
            {...rest}
        >
            {children}
        </ConditionalWrapper>
    );
}

function Input(props) {
    const { className, onFocus: providedOnFocus, ...rest } = props;

    const { buttonRef, isOpen, wasOptionOrButtonLastFocused, autoFocus } = useCombobox();

    function onFocus(event) {
        if (isFunction(providedOnFocus)) providedOnFocus(event);
        const button = buttonRef.current;
        if (isOpen || wasOptionOrButtonLastFocused || !button) return;
        button.click();
    }

    const ref = React.useRef(null);

    React.useEffect(() => {
        const input = ref.current;
        if (!autoFocus || !input) return;
        input.focus();
    }, [autoFocus]);

    return (
        <HUICombobox.Input
            ref={ref}
            className={mergeClasses(
                'h-[36px] min-w-0 flex-grow rounded-md border-none bg-transparent px-2 focus:border-primary-500 focus:ring-primary-500 group-focus-within:border-transparent group-focus-within:ring-0 sm:text-sm',
                className
            )}
            autoComplete="off"
            onFocus={onFocus}
            {...rest}
        />
    );
}

const transition = getPopInProps();

function Options(props) {
    const { className, ...rest } = props;
    const { inPortal, isOpen, animated, optionsRef } = useCombobox();
    const baseClassName =
        'rounded-md bg-white shadow-lg py-2 border border-neutral-100 overflow-auto cursor-default max-h-60';

    return (
        <ConditionalWrapper element={Popover.Panel} condition={inPortal} className={baseClassName}>
            <ConditionalWrapper
                element={Transition}
                condition={!inPortal && animated}
                as={React.Fragment}
                show={isOpen}
                {...transition}
            >
                <HUICombobox.Options
                    ref={optionsRef}
                    className={mergeClasses(
                        cn(!inPortal && `${baseClassName} absolute left-0 top-full z-10 w-full translate-y-2`),
                        className
                    )}
                    {...rest}
                />
            </ConditionalWrapper>
        </ConditionalWrapper>
    );
}

function Option(props) {
    const { className, value, children, ...rest } = props;

    return (
        <HUICombobox.Option value={value} as={React.Fragment}>
            {({ active, selected }) => (
                <li
                    className={mergeClasses(
                        cn(active && 'bg-primary-100 text-primary-700', 'py-1.5 px-4 text-sm'),
                        className
                    )}
                    {...rest}
                >
                    {isFunction(children) ? children({ active, selected }) : children}
                </li>
            )}
        </HUICombobox.Option>
    );
}

function Label(props) {
    const { className, ...rest } = props;

    const { hasError } = useCombobox();

    return (
        <HUICombobox.Label
            className={mergeClasses(
                cn(hasError ? 'text-red-800' : 'text-neutral-700', 'mb-1 block text-sm font-medium'),
                className
            )}
            {...rest}
        />
    );
}

function Helper(props) {
    const { as = 'p', className, id: providedId, children, ...rest } = props;
    const { helperId, hasError } = useCombobox();
    const id = providedId ?? helperId;

    const helper = React.createElement(
        as,
        {
            className: mergeClasses('mt-2 text-sm text-neutral-500', className),
            id,
            ...rest
        },
        children
    );

    return hasError ? null : helper;
}

function ErrorMessage(props) {
    const { as = 'p', className, id: providedId, children, ...rest } = props;
    const { errorMessageId } = useCombobox();
    const id = providedId ?? errorMessageId;

    return React.createElement(
        as,
        {
            className: mergeClasses('mt-1 text-sm text-red-800', className),
            id,
            ...rest
        },
        children
    );
}

export default function Container(props) {
    const {
        inPortal = true,
        animated = true,
        autoFocus,
        error,
        hasError: providedHasError,
        as = 'div',
        className,
        children,
        disabled,
        ...rest
    } = props;

    const errorMessageId = React.useId();
    const helperId = React.useId();

    const hasError = providedHasError ?? Boolean(error);

    // Open on focus logic
    // TODO: replace with `openOnFocus: true` when the headlessUI supports it
    const buttonRef = React.useRef(null);
    const optionsRef = React.useRef(null);
    const [wasOptionOrButtonLastFocused, setwasOptionOrButtonLastFocused] = React.useState(false);

    useDocumentEvent('focusin', () => {
        const { activeElement } = document;
        const options = optionsRef.current;
        const wasOptionLastFocused = options?.contains(activeElement);
        const button = buttonRef.current;
        const wasButtonLastFocused = button === activeElement;
        setwasOptionOrButtonLastFocused(wasOptionLastFocused || wasButtonLastFocused);
    });

    return (
        <HUICombobox as={as} disabled={disabled} className={mergeClasses('w-full', className)} {...rest}>
            {({ open: isOpen }) => (
                <ComboboxContext.Provider
                    value={{
                        isOpen,
                        disabled,
                        inPortal,
                        animated,
                        error,
                        hasError,
                        errorMessageId,
                        helperId,
                        buttonRef,
                        optionsRef,
                        wasOptionOrButtonLastFocused,
                        autoFocus
                    }}
                >
                    {children}
                </ComboboxContext.Provider>
            )}
        </HUICombobox>
    );
}

function Button(props) {
    const { buttonRef } = useCombobox();
    return <HUICombobox.Button {...props} ref={buttonRef} />;
}

function EmptyResults(props) {
    const { query, addValue } = props;
    return (
        <Button
            className={cn(
                'w-full py-2 px-4 text-sm flex gap-2 justify-center text-neutral-700',
                query ? 'hover:bg-primary-50 hover:text-primary-500' : 'cursor-default'
            )}
            onClick={addValue}
        >
            {query ? (
                <>
                    {_t.global.add()}
                    <Icon icon="material-symbols:add" width="1.3rem" height="1.3rem" />
                </>
            ) : (
                _t.global.noResults()
            )}
        </Button>
    );
}

function LoadingResults() {
    return <span className="py-2 px-4 text-sm text-neutral-700">{_t.global.loading()}</span>;
}

const defaultMakeReactKey = (option) => (isString(option) ? option : option.id ?? option.value);

const defaultFilterFn = (query, option) =>
    isString(option) ? option.toLowerCase().includes(query.toLowerCase()) : true;

function Full(props) {
    const {
        query: controlledQuery,
        defaultQuery = '',
        onQueryChange,
        onInputFocus,
        placeholder,
        label,
        options = [],
        error,
        helper,
        isLoading = false,
        filter = true,
        filterFn = defaultFilterFn,
        accessValue = (v) => v,
        displayValue = (v) => v,
        makeReactKey = defaultMakeReactKey,
        noBorder = false,
        disabled,
        children,
        nullable,
        name,
        value,
        elementsClassName,
        inputClassName,
        addValue,
        ...rest
    } = props;

    const isQueryControlled = controlledQuery != null;

    const [_query, setQuery] = React.useState(defaultQuery);
    const query = isQueryControlled ? controlledQuery : _query;

    const hasFilter = filter && query !== '';
    const boundFilter = filterFn.bind(null, query);
    const filteredOptions = hasFilter ? options.filter(boundFilter) : options;
    const hasResults = filteredOptions.length > 0;
    const providedChildren = isFunction(children) ? children({ filteredOptions, options, hasResults }) : children;

    function handleQueryChange(event) {
        const { value } = event.target;
        if (!isQueryControlled) setQuery(value);
        if (isFunction(onQueryChange)) onQueryChange(value);
    }

    return (
        <Container error={error} disabled={disabled} name={name} value={value} nullable={nullable} {...rest}>
            {label && <Label>{label}</Label>}
            <Elements noBorder={noBorder} className={elementsClassName}>
                <Input
                    value={query}
                    onChange={handleQueryChange}
                    onFocus={onInputFocus}
                    placeholder={placeholder ?? _t.components.select.selectOption()}
                    displayValue={(selectedOption) =>
                        // The combobox can also have no options selected
                        selectedOption ? utilizeAccessor(displayValue, selectedOption) : query
                    }
                    aria-describedby={useCombobox.helperId}
                    aria-errormessage={useCombobox.errorMessageId}
                    className={inputClassName}
                />
                {nullable && !value && !query && <input type="hidden" name={name} value={undefined} />}
                <BouncingDots
                    aria-hidden
                    className={cn(
                        isLoading ? 'opacity-100' : 'opacity-0',
                        'pointer-events-none absolute inset-y-0 right-8 my-auto w-4 -translate-y-0.5 fill-primary-500 transition-opacity'
                    )}
                />

                <Button className="flex w-11 flex-shrink-0 items-center justify-center md:w-9">
                    <ChevronUpDownIcon className="h-5 w-5 text-neutral-500" />
                </Button>

                <Options>
                    {providedChildren ??
                        (hasResults ? (
                            filteredOptions.map((option) => {
                                return (
                                    <Option
                                        className={(prev) => `${prev} flex items-center`}
                                        key={utilizeAccessor(makeReactKey, option)}
                                        value={utilizeAccessor(accessValue, option)}
                                    >
                                        {({ selected, active }) => (
                                            <React.Fragment>
                                                <CheckIcon
                                                    className={cn(
                                                        selected ? 'visible' : 'invisible',
                                                        active ? 'text-primary-700' : 'text-primary-600',
                                                        '-ml-2 mr-2 h-5 w-5'
                                                    )}
                                                />
                                                {/* Get value first, then apply the same logic in input to display it */}
                                                {utilizeAccessor(displayValue, utilizeAccessor(accessValue, option))}
                                            </React.Fragment>
                                        )}
                                    </Option>
                                );
                            })
                        ) : isLoading ? (
                            <LoadingResults />
                        ) : (
                            <EmptyResults query={query} addValue={addValue} />
                        ))}
                </Options>
            </Elements>
            {helper && <Helper>{helper}</Helper>}
            {error && <ErrorMessage>{error}</ErrorMessage>}
        </Container>
    );
}

Container.Elements = Elements;
Container.Input = Input;
Container.Button = Button;
Container.Options = Options;
Container.Option = Option;
Container.Label = Label;
Container.ErrorMessage = ErrorMessage;
Container.Full = Full;
Container.EmptyResults = EmptyResults;
